import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { logErr } from "../lib/utils-trel";
import { ChevronLeft, ChevronRight, Check, Sparkles, Award, MessageCircle, ClipboardList, LayoutDashboard, ShieldCheck, Loader2 } from "lucide-react";

const NAVY = "#071A2D";
const IVORY = "#FFF8EE";
const EMERALD = "#064E3B";
const GOLD = "#C9A45C";
const OCEAN = "#00AEEF";

const PAGES = [
  { n: 1, key: "page1_acknowledged", title: "Welcome to TREL", subtitle: "Our mission. Your role. The customer-first standard.", icon: Sparkles, color: OCEAN },
  { n: 2, key: "page2_acknowledged", title: "Customer Service Excellence", subtitle: "Premium communication, response time, and problem-solving.", icon: MessageCircle, color: EMERALD },
  { n: 3, key: "page3_acknowledged", title: "Experience Management", subtitle: "Understand requests. Build moments. Manage details.", icon: ClipboardList, color: GOLD },
  { n: 4, key: "page4_acknowledged", title: "TREL Dashboard Training", subtitle: "Your tools: profile, requests, messages, proposals, earnings.", icon: LayoutDashboard, color: NAVY },
  { n: 5, key: "standards_agreed",  title: "TREL Standards Agreement", subtitle: "Agree to our marketplace standards to activate.", icon: ShieldCheck, color: EMERALD },
];

export default function ConciergeOnboarding() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [cert, setCert] = useState(null);
  const [page, setPage] = useState(1);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/providers/certification");
      setCert(data);
      setPage(Math.max(1, Math.min(5, data.current_page || 1)));
    } catch (e) {
      logErr("providers/certification/load", e);
      // wizard still works with defaults
      setCert({ current_page: 1 });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveProgress = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.post("/providers/certification", next);
      setCert(data);
    } finally { setSaving(false); }
  };

  const acknowledgeAndNext = async () => {
    const cur = PAGES.find(p => p.n === page);
    if (!cur) return;
    const next = page + 1;
    await saveProgress({
      current_page: Math.min(5, next),
      [cur.key]: true,
    });
    setPage(Math.min(5, next));
  };

  const back = async () => {
    const prev = Math.max(1, page - 1);
    setPage(prev);
    await saveProgress({ current_page: prev });
  };

  const completeAndFinish = async () => {
    await saveProgress({ current_page: 5, standards_agreed: true });
    if (refresh) { try { await refresh(); } catch (e) { logErr("auth/refresh", e); } }
    nav("/provider", { replace: true });
  };

  if (user && user.role !== "provider" && user.role !== "admin") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <h1 className="font-serif text-3xl" style={{ color: NAVY }}>This training is for TREL Concierges.</h1>
        <Link to="/" className="btn-coral mt-6 inline-block">Return home</Link>
      </div>
    );
  }

  const cur = PAGES.find(p => p.n === page) || PAGES[0];
  const Icon = cur.icon;
  const completedAt = cert?.completed_at;

  return (
    <div className="min-h-screen" style={{ background: IVORY }} data-testid="page-concierge-onboarding">
      {/* Top progress bar */}
      <div className="border-b bg-white" style={{ borderColor: "rgba(7,26,45,0.08)" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-4">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>
            TREL Certified Training
          </div>
          <div className="text-xs" style={{ color: "rgba(7,26,45,0.55)" }} data-testid="onboarding-step-indicator">
            Step {page} of 5
          </div>
        </div>
        {/* Progress segments */}
        <div className="max-w-4xl mx-auto px-5 sm:px-8 pb-4">
          <div className="flex gap-1.5">
            {PAGES.map((p) => (
              <div
                key={p.n}
                className="flex-1 h-1.5 rounded-full"
                style={{ background: p.n <= page ? EMERALD : "rgba(7,26,45,0.10)" }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        {completedAt ? (
          <CompletionCard onContinue={() => nav("/provider", { replace: true })}/>
        ) : (
          <article
            className="rounded-[28px] bg-white border shadow-luxury-lg overflow-hidden"
            style={{ borderColor: "rgba(7,26,45,0.08)" }}
            data-testid={`onboarding-page-${page}`}
          >
            <div className="p-7 sm:p-10" style={{ background: `linear-gradient(135deg, ${cur.color}10 0%, transparent 70%)` }}>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${cur.color}1A`, color: cur.color }}>
                  <Icon size={22}/>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: cur.color }}>Page {cur.n}</div>
                  <h1 className="font-serif mt-1 leading-tight" style={{ color: NAVY, fontSize: "clamp(1.6rem, 3vw, 2.4rem)" }}>{cur.title}</h1>
                  <p className="mt-3 text-sm sm:text-base" style={{ color: "rgba(7,26,45,0.72)" }}>{cur.subtitle}</p>
                </div>
              </div>
            </div>

            <div className="px-7 sm:px-10 py-7 sm:py-9 space-y-5 text-[15px] leading-relaxed" style={{ color: "rgba(7,26,45,0.82)" }}>
              {page === 1 && <Page1/>}
              {page === 2 && <Page2/>}
              {page === 3 && <Page3/>}
              {page === 4 && <Page4/>}
              {page === 5 && <Page5/>}
            </div>

            {/* Footer nav */}
            <div className="px-7 sm:px-10 py-5 border-t flex items-center justify-between" style={{ borderColor: "rgba(7,26,45,0.08)" }}>
              <button
                onClick={back}
                disabled={page === 1}
                className="inline-flex items-center gap-1.5 text-sm font-medium disabled:opacity-30"
                style={{ color: NAVY }}
                data-testid="onboarding-back"
              >
                <ChevronLeft size={16}/> Back
              </button>
              {page < 5 ? (
                <button
                  onClick={acknowledgeAndNext}
                  disabled={saving}
                  className="btn-coral inline-flex items-center gap-2"
                  data-testid="onboarding-next"
                >
                  {saving ? <Loader2 size={16} className="animate-spin"/> : <Check size={16}/>}
                  I understand · Continue
                </button>
              ) : (
                <button
                  onClick={completeAndFinish}
                  disabled={saving}
                  className="btn-coral inline-flex items-center gap-2"
                  data-testid="onboarding-finish"
                >
                  {saving ? <Loader2 size={16} className="animate-spin"/> : <Award size={16}/>}
                  Agree & Become TREL Certified
                </button>
              )}
            </div>
          </article>
        )}
      </div>
    </div>
  );
}

const CompletionCard = ({ onContinue }) => (
  <div className="rounded-[28px] bg-white border p-10 sm:p-14 text-center shadow-luxury-lg" style={{ borderColor: `${GOLD}55` }} data-testid="onboarding-complete">
    <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center" style={{ background: `${GOLD}22`, color: GOLD }}>
      <Award size={28}/>
    </div>
    <div className="text-[11px] uppercase tracking-[0.22em] font-semibold mt-5" style={{ color: GOLD }}>You&apos;re TREL Certified</div>
    <h1 className="font-serif mt-3" style={{ color: NAVY, fontSize: "clamp(1.8rem, 3vw, 2.4rem)" }}>
      Welcome to the verified concierge network.
    </h1>
    <p className="mt-4 text-base" style={{ color: "rgba(7,26,45,0.65)" }}>
      You can now accept bookings and create unforgettable experiences. Your TREL Certified badge is visible to customers.
    </p>
    <button onClick={onContinue} className="btn-coral mt-7 inline-flex items-center gap-2">
      Open my dashboard <ChevronRight size={16}/>
    </button>
  </div>
);

/* ---- Page bodies ---- */
const Bullet = ({ children }) => (
  <li className="flex items-start gap-3">
    <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: EMERALD }}/>
    <span>{children}</span>
  </li>
);

const Page1 = () => (
  <>
    <p><strong>Our mission:</strong> TREL is the AI-powered concierge marketplace for unforgettable lifestyle, entertainment, travel, and event moments. We connect customers with trusted Experience Concierges who plan, host, and manage the entire experience.</p>
    <p><strong>What we expect from you:</strong></p>
    <ul className="space-y-2">
      <Bullet>Create premium, personalized experiences — never generic packages.</Bullet>
      <Bullet>Communicate quickly, clearly, and professionally with every customer.</Bullet>
      <Bullet>Coordinate every detail so the customer doesn&apos;t have to.</Bullet>
      <Bullet>Treat each booking like your most important moment.</Bullet>
    </ul>
  </>
);

const Page2 = () => (
  <>
    <p><strong>The TREL service standard.</strong> Customers come to TREL because they don&apos;t want to plan alone. Your job is to make them feel completely taken care of.</p>
    <ul className="space-y-2">
      <Bullet><strong>Respond within 4 hours</strong> during business hours. Speed builds trust.</Bullet>
      <Bullet>Set <strong>clear expectations</strong> on what&apos;s included, timing, and pricing. No surprises at the door.</Bullet>
      <Bullet>If something changes, <strong>tell the customer first</strong>, not after.</Bullet>
      <Bullet>Solve problems <strong>before they escalate</strong>. Offer alternatives, refunds, or apologies fast.</Bullet>
      <Bullet>Be warm. The luxury is in the human touch, not just the venue.</Bullet>
    </ul>
  </>
);

const Page3 = () => (
  <>
    <p><strong>Build an unforgettable moment from a request.</strong></p>
    <ul className="space-y-2">
      <Bullet><strong>Understand the vision</strong>: read the customer&apos;s request twice. Ask clarifying questions on date, guests, budget, dietary needs, and the special touch they want.</Bullet>
      <Bullet><strong>Design the timeline</strong>: arrival, signature moments, surprises, transport between venues, and a graceful end.</Bullet>
      <Bullet><strong>Coordinate vendors</strong>: chefs, drivers, photographers, venues. Confirm 48h ahead. Have a backup for every key vendor.</Bullet>
      <Bullet><strong>Price transparently</strong>: itemize what&apos;s included, your concierge service fee, and the all-in customer estimate.</Bullet>
      <Bullet>Use the <strong>AI Experience Assistant</strong> (TREL+) to turn any request into a polished proposal in seconds.</Bullet>
    </ul>
  </>
);

const Page4 = () => (
  <>
    <p><strong>Your dashboard is your business operations center.</strong></p>
    <ul className="space-y-2">
      <Bullet><strong>Profile</strong>: keep your bio, city, specialties, and photos current. This is what customers see first.</Bullet>
      <Bullet><strong>Requests &amp; Bookings</strong>: accept, message, update status (Pending → Confirmed → Completed), and mark complete promptly.</Bullet>
      <Bullet><strong>Messages</strong>: open with every booked customer. Keep all coordination on-platform for safety.</Bullet>
      <Bullet><strong>Booking Settings</strong>: choose deposit / full-payment, set cancellation policy, and free-cancellation window.</Bullet>
      <Bullet><strong>Proposals</strong>: generate Experience Plans, edit your service fee, and mark them Sent / Accepted.</Bullet>
      <Bullet><strong>Earnings</strong>: track payouts (80% per booking) and tips (100%). Same-day payouts after completion.</Bullet>
    </ul>
  </>
);

const Page5 = () => (
  <>
    <p><strong>By becoming TREL Certified, you agree to the TREL Standards.</strong></p>
    <ul className="space-y-2">
      <Bullet><strong>Professional behavior</strong> at every step — on chat, in person, and after the experience.</Bullet>
      <Bullet><strong>Accurate offerings</strong>. What you describe is what the customer gets.</Bullet>
      <Bullet><strong>Customer respect</strong>: no discrimination, harassment, or pressure tactics.</Bullet>
      <Bullet><strong>Reliable communication</strong>: stay on-platform, respond to messages, and update bookings promptly.</Bullet>
      <Bullet><strong>Marketplace standards</strong>: honor your cancellation and deposit policy; refund fairly when you cancel.</Bullet>
    </ul>
    <p className="mt-5 text-sm" style={{ color: "rgba(7,26,45,0.55)" }}>
      Repeated standards violations may result in suspension or removal from the marketplace.
    </p>
  </>
);
